/**
 * WorkflowPendingPage - Submenu "Pendências" do módulo Workflow
 * -----------------------------------------------------------------------------
 * Lista as aprovações e tarefas humanas do processo com filtros rápidos
 * (Minhas, Abertas, Vencidas, Bloqueantes, Resolvidas) e por processo, mais o
 * atalho "Abrir registro", que abre a ficha oficial do registro aguardando o
 * aceite. A resolução (aprovar/reprovar/concluir) reaproveita
 * `useWorkflowHumanTasks().resolve`; o banco revalida tenant e permissão.
 *
 * Permissões: ver a página = `workflow_pending`; decidir = `workflow_approvals`.
 *
 * @version 1.2.0
 * RELEASE: 2026-10-06 v1.2.0 - Botões Reprovar/Aprovar/Concluir da linha de
 *          decisão alinhados aos mesmos estilos da aba Workflow do modal de
 *          negócios (Reprovar em destructive/10 suave; Aprovar/Concluir sólidos
 *          em success), com ícones de 3,5 px como no modal.
 * RELEASE: 2026-10-06 v1.1.0 - Filtros rápidos e botão "Decidir" com estilo suave
 *          (fundo primary/10 + texto primary) em vez do preto sólido, para chamar
 *          menos atenção; padrão pill mantido.
 * RELEASE: 2026-10-06 v1.0.0 - Criação inicial (submenus do Workflow).
 */

import { Fragment, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  AlertTriangle,
  CheckCircle2,
  ClipboardCheck,
  Clock,
  ExternalLink,
  Lock,
  User,
  XCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { SmartAvatar } from '@/components/ui/smart-avatar';
import { PaginationFooter } from '@/components/ui/pagination-footer';
import {
  KpiCard,
  ListPageContent,
  ListPageFiltersBar,
  ListPageFooter,
  ListPageHeader,
  ListPageKpis,
  ListPageLayout,
} from '@/components/layout/list-page';
import { WorkflowDefinitionFilter } from '@/components/workflow/WorkflowDefinitionFilter';
// v1.1.0: estilo pill oficial dos filtros rápidos das listagens
import { QUICK_FILTER_PILL } from '@/components/layout/list-page/quickFilterStyles';
import {
  WORKFLOW_ENTITY_LABEL,
  canOpenWorkflowRecord,
  useWorkflowRecordOpener,
} from '@/components/workflow/WorkflowRecordOpener';
import { useWorkflowHumanTasks, type WorkflowHumanTask } from '@/hooks/useWorkflowHumanTasks';
import { useWorkflowOverviewKpis } from '@/hooks/useWorkflowOverviewKpis';
import { usePermissions } from '@/hooks/usePermissions';
import { formatUuidShort } from '@/lib/formatters';
import { cn } from '@/lib/utils';

/** Filtros rápidos aceitos (também via ?filtro= na URL) */
type FiltroRapido = 'abertas' | 'minhas' | 'vencidas' | 'bloqueantes' | 'resolvidas';
const FILTROS: Array<{ key: FiltroRapido; label: string }> = [
  { key: 'abertas', label: 'Abertas' },
  { key: 'minhas', label: 'Minhas' },
  { key: 'vencidas', label: 'Vencidas' },
  { key: 'bloqueantes', label: 'Bloqueantes' },
  { key: 'resolvidas', label: 'Resolvidas' },
];

/** Data/hora curta pt-BR */
const dataHora = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '—';

/** Rótulo da situação da pendência */
const SITUACAO: Record<string, string> = {
  pendente: 'Pendente',
  aprovado: 'Aprovada',
  reprovado: 'Reprovada',
  concluido: 'Concluída',
  cancelado: 'Cancelada',
};

export default function WorkflowPendingPage() {
  const [params, setParams] = useSearchParams();
  const filtroUrl = params.get('filtro') as FiltroRapido | null;
  const [filtro, setFiltro] = useState<FiltroRapido>(
    FILTROS.some((f) => f.key === filtroUrl) ? (filtroUrl as FiltroRapido) : 'abertas',
  );
  const [processo, setProcesso] = useState<string | null>(null);
  const [pagina, setPagina] = useState(1);
  const [tamanho, setTamanho] = useState(25);
  const [justificativas, setJustificativas] = useState<Record<string, string>>({});
  const [expandida, setExpandida] = useState<string | null>(null);

  const { canEdit } = usePermissions();
  const podeResolver = canEdit('workflow_approvals');
  const { kpis } = useWorkflowOverviewKpis();
  const { openRecord, element: fichas } = useWorkflowRecordOpener();

  // Mantém a URL em sincronia com o filtro rápido (permite links diretos)
  useEffect(() => {
    if (params.get('filtro') !== filtro) {
      const next = new URLSearchParams(params);
      next.set('filtro', filtro);
      setParams(next, { replace: true });
    }
  }, [filtro]); // eslint-disable-line react-hooks/exhaustive-deps

  // Volta à primeira página ao trocar filtros
  useEffect(() => setPagina(1), [filtro, processo, tamanho]);

  const { tasks, isLoading, resolve } = useWorkflowHumanTasks({
    onlyMine: filtro === 'minhas',
    onlyOverdue: filtro === 'vencidas',
    onlyBlocking: filtro === 'bloqueantes',
    onlyResolved: filtro === 'resolvidas',
    definitionId: processo,
    limit: 200,
  });

  // Paginação local (a RPC limita a 200 itens por consulta)
  const pagina_itens = useMemo(
    () => tasks.slice((pagina - 1) * tamanho, pagina * tamanho),
    [tasks, pagina, tamanho],
  );

  /** Prazo vencido = flag de SLA ou data passada ainda pendente */
  const vencida = (t: WorkflowHumanTask) =>
    t.status === 'pendente' && (t.sla_violated || (!!t.due_at && new Date(t.due_at) < new Date()));

  return (
    <ListPageLayout>
      <ListPageHeader
        title="Pendências do processo"
        description="Aprovações e tarefas que seguram o avanço dos processos."
      />

      <ListPageKpis>
        <KpiCard color="blue" icon={ClipboardCheck} label="Abertas" value={kpis.pendencias_abertas}
          active={filtro === 'abertas'} onClick={() => setFiltro('abertas')} />
        <KpiCard color="purple" icon={User} label="Minhas" value={kpis.minhas_pendencias}
          active={filtro === 'minhas'} onClick={() => setFiltro('minhas')} />
        <KpiCard color="red" icon={AlertTriangle} label="Vencidas" value={kpis.pendencias_vencidas}
          active={filtro === 'vencidas'} onClick={() => setFiltro('vencidas')} />
        <KpiCard color="orange" icon={Lock} label="Bloqueantes" value={kpis.pendencias_bloqueantes}
          active={filtro === 'bloqueantes'} onClick={() => setFiltro('bloqueantes')} />
      </ListPageKpis>

      <ListPageFiltersBar>
        {FILTROS.map((f) => (
          <Button
            key={f.key}
            size="sm"
            variant="outline"
            // v1.1.0: pill suave — ativo usa fundo primary/10 + texto primary (antes: preto sólido)
            className={cn(
              QUICK_FILTER_PILL,
              'h-6 px-2.5 text-[11px] sm:h-8 sm:text-xs',
              filtro === f.key && 'bg-primary/10 text-primary hover:bg-primary/15',
            )}
            onClick={() => setFiltro(f.key)}
          >
            {f.label}
          </Button>
        ))}
        <WorkflowDefinitionFilter value={processo} onChange={setProcesso} />
      </ListPageFiltersBar>

      <ListPageContent>
        <div className="overflow-auto">
          <table className="w-full min-w-[960px] text-xs">
            <thead className="bg-muted/40 text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Pendência</th>
                <th className="px-3 py-2 text-left font-medium">Processo / etapa</th>
                <th className="px-3 py-2 text-left font-medium">Registro</th>
                <th className="px-3 py-2 text-left font-medium">Responsável</th>
                <th className="px-3 py-2 text-left font-medium">Prazo</th>
                <th className="px-3 py-2 text-left font-medium">Situação</th>
                <th className="px-3 py-2 text-right font-medium">Ações</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr><td colSpan={7} className="px-3 py-8 text-center text-muted-foreground">Carregando…</td></tr>
              )}
              {!isLoading && pagina_itens.length === 0 && (
                <tr><td colSpan={7} className="px-3 py-8 text-center text-muted-foreground">Nenhuma pendência para estes filtros.</td></tr>
              )}

              {pagina_itens.map((t) => {
                const aberta = expandida === t.id;
                const pendente = t.status === 'pendente';
                return (
                  <Fragment key={t.id}>
                    <tr className={cn('border-t', vencida(t) && 'bg-destructive/5')}>
                      {/* Título + tipo + alertas */}
                      <td className="max-w-[260px] px-3 py-2">
                        <p className="truncate font-medium text-foreground">{t.title}</p>
                        <div className="mt-0.5 flex flex-wrap items-center gap-1">
                          <Badge variant="secondary" className="h-4 px-1.5 text-[10px]">
                            {t.kind === 'aprovacao' ? 'Aprovação' : 'Tarefa'}
                          </Badge>
                          {t.blocking && <Badge variant="outline" className="h-4 px-1.5 text-[10px]">Bloqueante</Badge>}
                          {vencida(t) && <Badge variant="destructive" className="h-4 px-1.5 text-[10px]">Vencida</Badge>}
                        </div>
                      </td>
                      <td className="max-w-[200px] px-3 py-2">
                        <p className="truncate text-foreground">{t.definition_name || '—'}</p>
                        <p className="truncate text-muted-foreground">{t.node_label || 'Etapa humana'}</p>
                      </td>
                      <td className="px-3 py-2">
                        <p className="text-foreground">{WORKFLOW_ENTITY_LABEL[t.root_entity_type] ?? t.root_entity_type}</p>
                        <p className="font-mono text-muted-foreground">{formatUuidShort(t.root_entity_id)}</p>
                      </td>
                      <td className="px-3 py-2">
                        {t.assignee_name ? (
                          <div className="flex items-center gap-2">
                            <SmartAvatar src={t.assignee_avatar_url ?? undefined} name={t.assignee_name} size="xs" />
                            <span className="max-w-[120px] truncate">{t.assignee_name}</span>
                          </div>
                        ) : (
                          <span className="text-muted-foreground">Sem responsável</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-muted-foreground">
                        <span className="inline-flex items-center gap-1">
                          <Clock className="h-3 w-3" /> {dataHora(t.due_at)}
                        </span>
                        <p className="text-[10px]">Criada {dataHora(t.created_at)}</p>
                      </td>
                      <td className="px-3 py-2">
                        <p className="text-foreground">{SITUACAO[t.status] ?? t.status}</p>
                        {t.resolved_by_name && (
                          <p className="truncate text-[10px] text-muted-foreground">
                            por {t.resolved_by_name} · {dataHora(t.resolved_at)}
                          </p>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex justify-end gap-1">
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 gap-1 text-xs"
                            disabled={!canOpenWorkflowRecord(t.root_entity_type)}
                            onClick={() => openRecord(t.root_entity_type, t.root_entity_id)}
                          >
                            <ExternalLink className="h-3.5 w-3.5" /> Abrir registro
                          </Button>
                          {/* v1.1.0: estilo suave (primary/10 + primary) no lugar do sólido */}
                          {pendente && (
                            <Button
                              size="sm"
                              variant="outline"
                              className={cn(
                                'h-7 text-xs',
                                !aberta && 'border-primary/20 bg-primary/10 text-primary hover:bg-primary/15',
                              )}
                              disabled={!podeResolver}
                              onClick={() => setExpandida(aberta ? null : t.id)}
                            >
                              {aberta ? 'Fechar' : 'Decidir'}
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>

                    {/* Linha de decisão (descrição + justificativa + ações) */}
                    {aberta && pendente && (
                      <tr className="bg-muted/20">
                        <td colSpan={7} className="space-y-2 px-3 py-3">
                          {t.description && <p className="text-xs text-muted-foreground">{t.description}</p>}
                          {t.kind === 'aprovacao' ? (
                            <>
                              <Textarea
                                rows={2}
                                placeholder="Justificativa (obrigatória para reprovar)"
                                value={justificativas[t.id] ?? ''}
                                maxLength={500}
                                onChange={(e) =>
                                  setJustificativas((a) => ({ ...a, [t.id]: e.target.value.slice(0, 500) }))
                                }
                              />
                              <div className="flex justify-end gap-2">
                                {/* v1.2.0: mesmos estilos dos botões da aba Workflow do modal de negócios */}
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 px-2.5 text-[11px] bg-destructive/10 text-destructive hover:bg-destructive/20 hover:text-destructive"
                                  disabled={resolve.isPending || !podeResolver}
                                  onClick={() => resolve.mutate({ id: t.id, decision: 'reprovado', note: justificativas[t.id] })}>
                                  <XCircle className="mr-1 h-3.5 w-3.5" /> Reprovar
                                </Button>
                                <Button size="sm"
                                  className="h-7 bg-success px-3 text-[11px] text-success-foreground hover:bg-success/90"
                                  disabled={resolve.isPending || !podeResolver}
                                  onClick={() => resolve.mutate({ id: t.id, decision: 'aprovado', note: justificativas[t.id] })}>
                                  <CheckCircle2 className="mr-1 h-3.5 w-3.5" /> Aprovar
                                </Button>
                              </div>
                            </>
                          ) : (
                            <div className="flex justify-end">
                              {/* v1.2.0: mesmo estilo do botão "Concluir" do modal de negócios */}
                              <Button size="sm"
                                className="h-7 bg-success px-3 text-[11px] text-success-foreground hover:bg-success/90"
                                disabled={resolve.isPending || !podeResolver}
                                onClick={() => resolve.mutate({ id: t.id, decision: 'concluido' })}>
                                <CheckCircle2 className="mr-1 h-3.5 w-3.5" /> Concluir
                              </Button>
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </ListPageContent>

      <ListPageFooter>
        <PaginationFooter
          totalItems={tasks.length}
          currentPage={pagina}
          pageSize={tamanho}
          onPageChange={setPagina}
          onPageSizeChange={setTamanho}
          pageSizeOptions={[10, 25, 50, 100]}
        />
      </ListPageFooter>

      {/* Fichas oficiais abertas pelo atalho "Abrir registro" */}
      {fichas}
    </ListPageLayout>
  );
}
